import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '../../stores/auth'
import { API_CONFIG } from '../../config'

// Создаем базовый URL для API
const apiBaseUrl = API_CONFIG.BASE_URL

// Экспортируем конфигурацию API для использования в других местах
export const API_BASE_URL = apiBaseUrl

const AUTH_ENDPOINT_FRAGMENTS = ['/auth/login', '/auth/request-password-reset', '/auth/reset-password']

let authLogoutInProgress = false

function handleUnauthorizedResponse(error: any) {
  if (error.response?.status !== 401) {
    return
  }

  const authStore = useAuthStore()
  const requestUrl = String(error.config?.url || '')
  const isAuthEndpoint = AUTH_ENDPOINT_FRAGMENTS.some((fragment) => requestUrl.includes(fragment))
  const hadAuthHeader = Boolean(error.config?.headers?.Authorization)

  // Only logout when an authenticated request was rejected (expired/invalid token).
  if (authLogoutInProgress || !authStore.isAuthenticated || !hadAuthHeader || isAuthEndpoint) {
    return
  }

  authLogoutInProgress = true
  authStore.logout()
  window.setTimeout(() => {
    authLogoutInProgress = false
  }, 1000)
}

function attachAuthInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    return config
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: any) => {
      handleUnauthorizedResponse(error)
      return Promise.reject(error)
    },
  )
}

/** Shared axios factory (auth token + 401 logout). Used by default api and agentApiInstance. */
export function createApiClient(options?: { timeout?: number }): AxiosInstance {
  const instance = axios.create({
    baseURL: apiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: options?.timeout ?? API_CONFIG.TIMEOUT,
  })
  attachAuthInterceptors(instance)
  return instance
}

const api = createApiClient()

// Axios instance for Hermes agent calls with a longer timeout
export const agentApiInstance = createApiClient({ timeout: 180000 })

// Определение конечных точек API
export const endpoints = {
  auth: {
    login: () => '/auth/login/access-token',
    verify2fa: () => '/auth/login/verify-2fa',
    testToken: () => '/auth/login/test-token',
    logout: () => '/logout',
    refreshToken: () => '/refresh-token',
    requestPasswordReset: () => '/auth/request-password-reset',
    resetPassword: () => '/auth/reset-password',
  },

  users: {
    list: ({ page, pageSize }: { page: number; pageSize: number }) => `/users/?page=${page}&pageSize=${pageSize}`,
    all: () => '/users/',
    one: (id: string) => `/users/${id}`,
    me: {
      profile: () => '/users/me',
      organizations: () => '/users/me/organizations',
      update: () => '/users/me',
      verifyPassword: () => '/users/me/verify-password',
    },
    create: () => '/users/',
    update: (id: string) => `/users/${id}`,
  },

  organizations: {
    create: () => '/org/',
    list: () => '/org/',
    one: (id: number) => `/org/${id}`,
    update: (id: number) => `/org/${id}`,
    statistics: (id: number) => `/org/${id}/statistics`,
  },

  projects: {
    list: ({ page, pageSize }: { page: number; pageSize: number }) =>
      `/odk-projects/?page=${page}&pageSize=${pageSize}`,
    all: () => '/odk-projects/',
    one: (id: number) => `/odk-projects/${id}`,
  },

  organizationProjects: {
    all: (orgId: number) => `/org/${orgId}/odk-projects/`,
    one: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}`,
    statistics: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/statistics`,
  },

  projectRoles: {
    usersInProject: (projectId: number, roleName?: string) =>
      `/project-roles/project/${projectId}/users${roleName ? '?role_name=' + roleName : ''}`,
    userProjects: (userId: number, roleName?: string) =>
      `/project-roles/user/${userId}/projects${roleName ? '?role_name=' + roleName : ''}`,
    userRolesInProject: (projectId: number, userId: number) =>
      `/project-roles/project/${projectId}/user/${userId}/roles`,
    assignRole: (projectId: number, userId: number, roleId: number) =>
      `/project-roles/project/${projectId}/user/${userId}/role/${roleId}`,
    removeRole: (projectId: number, userId: number, roleId: number) =>
      `/project-roles/project/${projectId}/user/${userId}/role/${roleId}`,
    allAssignments: () => '/project-roles/',
  },

  avatars: {
    get: () => '/avatars',
  },

  organizationOdkServers: {
    list: (orgId: number) => `/org/${orgId}/odk-servers/`,
    create: (orgId: number) => `/org/${orgId}/odk-servers/`,
    update: (orgId: number, serverId: number) => `/org/${orgId}/odk-servers/${serverId}`,
    delete: (orgId: number, serverId: number) => `/org/${orgId}/odk-servers/${serverId}`,
    setDefault: (orgId: number) => `/org/${orgId}/odk-servers/set-default`,
  },

  odkServers: {
    testConnection: (serverId: number) => `/odk-servers/${serverId}/test-connection`,
  },

  odkSync: {
    schedulerStatus: () => '/odk-sync-scheduler/status',
    pause: () => '/odk-sync-scheduler/pause',
    resume: () => '/odk-sync-scheduler/resume',
    healthCheck: () => '/odk-sync-scheduler/health-check',
    cursorsSummary: () => '/odk-sync-cursors/status/summary',
    cursorsList: () => '/odk-sync-cursors/',
    cursorReset: (cursorId: number) => `/odk-sync-cursors/${cursorId}/reset`,
    createCursor: () => '/odk-sync-cursors/',
    bulkSync: () => '/odk-odata-sync/bulk-sync',
    formSync: (cursorId: number) => `/odk-odata-sync/form/${cursorId}/sync`,
    // Новые endpoints для оптимизации на основе статуса форм
    checkFormStatuses: () => '/odk-sync-cursors/check-form-statuses',
    formStatusSummary: () => '/odk-sync-cursors/form-status-summary',
    bulkEnableCursors: () => '/odk-sync-cursors/bulk-enable-cursors',
    bulkDisableCursors: () => '/odk-sync-cursors/bulk-disable-cursors',
    // Auto-management endpoints
    toggleAutoManagement: (cursorId: number) => `/odk-sync-cursors/${cursorId}/toggle-auto-management`,
    bulkToggleAutoManagement: () => '/odk-sync-cursors/bulk-toggle-auto-management',
  },

  odkQc: {
    // QC Rules management
    rules: {
      list: () => '/odk-qc/rules',
      create: () => '/odk-qc/rules',
      update: (ruleId: number) => `/odk-qc/rules/${ruleId}`,
      delete: (ruleId: number) => `/odk-qc/rules/${ruleId}`,
      one: (ruleId: number) => `/odk-qc/rules/${ruleId}`,
    },
    // QC Validation
    validate: () => '/odk-qc/validate',
    // QC Results
    results: {
      list: () => '/odk-qc/results',
      forSubmission: (instanceId: string) => `/odk-qc/results?instance_id=${instanceId}`,
      bulkUpdate: () => '/odk-qc/results/bulk-update',
    },
    // QC Statistics
    stats: {
      summary: () => '/odk-qc/stats',
      dashboard: {
        project: (organizationId: number, projectId: number) =>
          `/odk-qc/dashboard/project?organization_id=${organizationId}&project_id=${projectId}`,
        form: (organizationId: number, xmlFormId: string) =>
          `/odk-qc/dashboard/form?organization_id=${organizationId}&xml_form_id=${xmlFormId}`,
      },
    },
  },

  roles: {
    all: () => '/roles/',
  },

  permissionGroups: {
    list: () => '/permission-groups/',
    create: () => '/permission-groups/',
    one: (id: number) => `/permission-groups/${id}`,
    update: (id: number) => `/permission-groups/${id}`,
    delete: (id: number) => `/permission-groups/${id}`,
    assignToUser: (userId: number, groupId: number) => `/permission-groups/users/${userId}/groups/${groupId}`,
    removeFromUser: (userId: number, groupId: number) => `/permission-groups/users/${userId}/groups/${groupId}`,
    assignMultipleToUser: (userId: number) => `/permission-groups/users/${userId}/groups/bulk/`,
    assignUserToProjects: (userId: number) => `/permission-groups/users/${userId}/projects/assign/`,
    getUserGroups: (userId: number) => `/permission-groups/users/${userId}/groups/`,
    getUserGroupsByProject: (userId: number) => `/permission-groups/users/${userId}/project-groups/`,
    getGroupUsers: (groupId: number) => `/permission-groups/${groupId}/users/`,
    health: () => '/permission-groups/health',
  },

  organizationProjectForms: {
    list: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}/forms/`,
    details: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}`,
    xml: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}.xml`,
    xlsx: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}.xlsx`,
    updateState: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}`,
    delete: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}`,
    listAttachments: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/attachments`,
    downloadAttachment: (orgId: number, projectId: number, xmlFormId: string, filename: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/attachments/${filename}`,
    uploadAttachment: (orgId: number, projectId: number, xmlFormId: string, filename: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/attachments/${filename}`,
    deleteAttachment: (orgId: number, projectId: number, xmlFormId: string, filename: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/attachments/${filename}`,
    fields: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/fields`,
    syncAll: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}/sync-forms`,
    publishDraft: (projectId: number, xmlFormId: string) => `/odk-forms/${projectId}/${xmlFormId}/draft/publish`,
    analytics: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${xmlFormId}/analytics`,
    versions: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${encodeURIComponent(xmlFormId)}/versions`,
    versionXlsx: (orgId: number, projectId: number, xmlFormId: string, version: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${encodeURIComponent(xmlFormId)}/versions/${encodeURIComponent(version)}.xlsx`,
    publicLinks: (orgId: number, projectId: number, xmlFormId: string) =>
      `/org/${orgId}/odk-projects/${projectId}/forms/${encodeURIComponent(xmlFormId)}/public-links`,
    uploadXlsform: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}/upload-xlsform`,
  },

  odkExport: {
    formats: () => '/odk-export/formats',
    convert: (taskId?: string) => `/odk-export/convert${taskId ? `?task_id=${taskId}` : ''}`,
    status: (taskId: string) => `/odk-export/status/${taskId}`,
    download: (taskId: string) => `/odk-export/download/${taskId}`,
    deleteExport: (orgId: number, projectId: number, formId: string, exportId: string) =>
      `/odk-export/${orgId}/${projectId}/${formId}/${exportId}`,
    downloadOdata: () => '/odk-export/download-odata',
    odataSyncCheck: () => '/odk-export/odata-sync-check',
    listOdataSubmissions: (orgId: number, projectId: number, formId: string) =>
      `/odk-export/list-odata?organization_id=${orgId}&project_id=${projectId}&form_id=${formId}`,
    listExports: (orgId: number, projectId: number, formId: string) =>
      `/odk-export/list/${orgId}/${projectId}/${formId}`,
    getLanguages: (orgId: number, projectId: number, formId: string) =>
      `/odk-export/languages/${orgId}/${projectId}/${formId}`,
    geoCheck: (orgId: number, projectId: number, formId: string) =>
      `/odk-export/geo-check/${orgId}/${projectId}/${formId}`,
    parseLabels: (orgId: number, projectId: number, formId: string) =>
      `/organization-odk-projects/${orgId}/forms/${formId}/parse-labels`,
  },

  websocket: {
    syncStatus: (organizationId: number) => `/api/v1/ws/sync-status/${organizationId}`,
    projectUpdates: (organizationId: number, projectId: number) =>
      `/api/v1/ws/project/${projectId}/updates?organization_id=${organizationId}`,
    organizationUpdates: (organizationId: number) => `/api/v1/ws/org/${organizationId}/updates`,
  },

  agent: {
    subscription: (orgId: number) => `/org/${orgId}/agent/subscription`,
    chat: (orgId: number) => `/org/${orgId}/agent/chat`,
    reportSummary: (orgId: number) => `/org/${orgId}/agent/reports/summary`,
    reportAnalytical: (orgId: number) => `/org/${orgId}/agent/reports/analytical`,
    reportAnalyticalStatus: (orgId: number, jobId: string) => `/org/${orgId}/agent/reports/analytical/${jobId}`,
    reportTemplates: (orgId: number) => `/org/${orgId}/agent/report-templates`,
    reportTemplate: (orgId: number, templateId: number) => `/org/${orgId}/agent/report-templates/${templateId}`,
    reportTemplatesSeed: (orgId: number) => `/org/${orgId}/agent/report-templates/seed`,
    reportFormOptions: (orgId: number) => `/org/${orgId}/agent/report-form-options`,
    reportTrigger: (orgId: number) => `/org/${orgId}/agent/reports/trigger`,
    reportRuns: (orgId: number) => `/org/${orgId}/agent/reports/runs`,
    reportRun: (orgId: number, runId: number) => `/org/${orgId}/agent/reports/runs/${runId}`,
    reportRunDeliver: (orgId: number, runId: number) => `/org/${orgId}/agent/reports/runs/${runId}/deliver`,
    qcSuggest: (orgId: number) => `/org/${orgId}/agent/qc/suggest-rule`,
    qcStarterPack: (orgId: number) => `/org/${orgId}/agent/qc/suggest-starter-pack`,
    xlsformAssist: (orgId: number) => `/org/${orgId}/agent/xlsform/assist`,
    xlsformAssistWithFile: (orgId: number) => `/org/${orgId}/agent/xlsform/assist-with-file`,
    xlsformJobStatus: (orgId: number, jobId: string) => `/org/${orgId}/agent/xlsform/jobs/${jobId}`,
    xlsformSessions: (orgId: number) => `/org/${orgId}/agent/xlsform/sessions`,
    xlsformSession: (orgId: number, sessionId: string) => `/org/${orgId}/agent/xlsform/sessions/${sessionId}`,
    xlsformSessionCleanup: (orgId: number) => `/org/${orgId}/agent/xlsform/sessions/cleanup`,
    xlsformSessionSettings: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/settings`,
    xlsformSessionTimeline: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/timeline`,
    xlsformSessionValidation: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/validation`,
    xlsformSessionDiscard: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/discard`,
    xlsformSessionResume: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/resume`,
    xlsformSessionDownload: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/download.xlsx`,
    xlsformSessionImport: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/import-xlsx`,
    xlsformSessionForkRevision: (orgId: number, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/fork-revision`,
    xlsformPublish: (orgId: number) => `/org/${orgId}/agent/xlsform/publish`,
    xlsformPreviewXml: (orgId: number | string, sessionId: string) =>
      `/org/${orgId}/agent/xlsform/sessions/${sessionId}/preview-xml`,
    xlsformFormAttachment: (orgId: number | string, xmlFormId: string, filename: string) =>
      `/org/${orgId}/agent/xlsform/forms/${xmlFormId}/attachments/${filename}`,
    xlsformTestSubmission: (orgId: number | string, xmlFormId: string) =>
      `/org/${orgId}/agent/xlsform/forms/${xmlFormId}/test-submission`,
    formHealth: (orgId: number, xmlFormId: string) => `/org/${orgId}/agent/forms/${xmlFormId}/health`,
    formChat: (orgId: number, xmlFormId: string) => `/org/${orgId}/agent/forms/${xmlFormId}/chat`,
  },

  map: {
    forms: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/map/forms`,
    points: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/map/points`,
    pointsGeoJson: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/map/points.geojson`,
    settings: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/map/settings`,
    views: (orgId: number, projectId: number) => `/org/${orgId}/projects/${projectId}/map/views`,
    viewById: (orgId: number, projectId: number, viewId: number) =>
      `/org/${orgId}/projects/${projectId}/map/views/${viewId}`,
    tileConfig: (orgId: number) => `/org/${orgId}/map/tile-config`,
  },

  dashboardShares: {
    list: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}/dashboard-shares`,
    create: (orgId: number, projectId: number) => `/org/${orgId}/odk-projects/${projectId}/dashboard-shares`,
    revoke: (orgId: number, projectId: number, linkId: number) =>
      `/org/${orgId}/odk-projects/${projectId}/dashboard-shares/${linkId}`,
    update: (orgId: number, projectId: number, linkId: number) =>
      `/org/${orgId}/odk-projects/${projectId}/dashboard-shares/${linkId}`,
  },

  sharePublic: {
    session: (token: string) => `/share/${encodeURIComponent(token)}/session`,
    me: () => `/share/me`,
    forms: () => `/share/data/forms`,
    analytics: (xmlFormId: string) => `/share/data/forms/${encodeURIComponent(xmlFormId)}/analytics`,
  },
}

// Экспортируем экземпляр API по умолчанию
export default api
